import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { DataDisplayTable } from 'components/data-display';
import { FormCanvas } from 'components/form-fields';
import { DateToLocal } from 'components/dates';
import { ShowHide } from 'components/layout';
import { NotificationInline } from 'components/notifications';
import { StatusBlock } from 'components/status';
import { awaitingSignOffStatusBand, completedStatusBand, medicalInformationTopicKey, profileInformationTopicKey } from 'pages/employee-reviews/shared';
import { TopicSnapshot } from './snapshots';
import './CompletedReviewDetail.css';

const dateFormat = "DD MMM YYYY";
const dateTimeFormat = "DD MMM YYYY, HH:mm";
const inaccessibleTopicMessage = "You do not have permission to view the details of this topic";

// The renderer for each topic's frozen snapshot, keyed by the topic key the API returns. A
// topic with no renderer shows its heading and comment only, so a topic the backend records
// before the frontend presents it degrades rather than breaking the record.
const topicSnapshotRenderers = {
    [profileInformationTopicKey]: TopicSnapshot,
    [medicalInformationTopicKey]: TopicSnapshot
};

// Frontend copy describing how the review was signed off, keyed by the API's method key.
const signOffMethodSentences = {
    PHYSICAL: "Signed off via physical signature.",
    ONSCREEN: "Signed off via on-screen signature.",
    EMAIL: "Signed off via email sign-off.",
    APP: "Signed off via the Vision app."
};

// How each method reads while it is still waiting on the employee, keyed the same way. Only
// these two can ever be outstanding - a physical or on-screen sign-off is given during the
// review itself - so the other methods have no waiting phrase and never need one.
const awaitingSignOffMethodPhrases = {
    EMAIL: "email",
    APP: "the Vision app"
};

const emailSignOffMethodKey = "EMAIL";
const appSignOffMethodKey = "APP";

// Email and app sign-offs wait on the employee to sign after the manager has
// completed the review; the other methods are signed off during the review itself.
const employeeDrivenSignOffMethods = [emailSignOffMethodKey, appSignOffMethodKey];

// The read-only presentation of a completed review, shared by the manager-facing record and
// the driver's unauthenticated sign-off page. It renders only from the review it is given:
// no fetching, no route params and nothing that reads a signed-in user, so it is safe to
// render anonymously.
//
// showSignOff covers the Sign Off section as a whole: how it was signed, the signatures held
// against it and whether it is still outstanding. It is written for a manager reading a
// finished record. The driver signing through the emailed link is the subject of that section
// rather than its audience, and needs it ordered around the signature they are about to give,
// so that page turns this off and renders its own.
//
// signOffActions is where a caller places anything that acts on the sign-off. This component
// stays presentational and is rendered anonymously, so the controls themselves - and the
// permission checks behind them - belong to the calling page rather than here.
//
// title replaces the heading the record leads with. The emailed sign-off page is a page about
// the signature being given rather than a record being read, so it names itself as such.
export function CompletedReviewDetail(
    {
        review = null,
        loading = false,
        linkEmployee = true,
        showSignOff = true,
        signOffActions = null,
        title = null
    }
) {

    const awaitingEmployeeSignOff = review != null &&
        employeeDrivenSignOffMethods.includes(review.signOff?.method) &&
        review.signOff?.employeeSignedOffDate == null;

    const employeeSignature = review?.signOff?.employeeSignature;

    function buildTitleDisplay() {
        if (title != null) {
            return title;
        }

        return (
            <Fragment>
                {review?.reviewType?.name} - <DateToLocal format={dateFormat}>{review?.completedDate}</DateToLocal>
            </Fragment>
        );
    }

    function buildStatusDisplay() {
        if (awaitingEmployeeSignOff) {
            return <span className={awaitingSignOffStatusBand.className}>{awaitingSignOffStatusBand.label}</span>;
        }

        return <span className={completedStatusBand.className}>{completedStatusBand.label}</span>;
    }

    // What the Sign Off section leads with. A review still waiting on the employee says so
    // rather than claiming a sign-off that has not happened, and it reads off the same flag as
    // the Status field above so that the two can never contradict each other.
    function buildSignOffMethodSentence() {
        const signOff = review?.signOff;

        if (!awaitingEmployeeSignOff) {
            return signOffMethodSentences[signOff?.method] ?? null;
        }

        const methodPhrase = awaitingSignOffMethodPhrases[signOff?.method];

        if (methodPhrase == null) {
            return null;
        }

        // The emailed link is the only method with a destination worth naming, and the API holds
        // no address against any other one, so its presence is what decides the wording. Naming
        // it here lets a manager chasing a sign-off see where the link went without opening the
        // resend field.
        if (signOff.signOffEmailAddress == null) {
            return `Awaiting employee sign off via ${methodPhrase}.`;
        }

        return `Awaiting employee sign off via ${methodPhrase} to ${signOff.signOffEmailAddress}.`;
    }

    const signOffMethodSentence = buildSignOffMethodSentence();

    // The employee name links to their own Reviews tab, opening in a new tab so the
    // completed record stays put. Matches the complete-review wizard's header. Falls
    // back to plain text while loading, or if the record carries no employee id. The
    // link target is an authenticated route, so a caller rendering this to someone who
    // is not signed in turns it off rather than sending them into the login flow.
    function buildEmployeeDisplay() {
        const employeeId = review?.employee?.id;
        const employeeName = review?.employee?.name;

        return (
            <ShowHide
                evaluator={linkEmployee && !loading && employeeId != null}
                show={(
                    <Link to={`/employee/${employeeId}/reviews`} target="_blank" rel="noopener noreferrer">{employeeName}</Link>
                )}
                hide={employeeName}
            />
        );
    }

    function buildMetaFields() {
        return [
            { key: "employee", header: "Employee", value: buildEmployeeDisplay() },
            { key: "completed", header: "Completed", value: <DateToLocal format={dateTimeFormat}>{review?.completedDate}</DateToLocal> },
            { key: "completed-by", header: "Completed By", value: review?.completedBy },
            { key: "status", header: "Status", value: buildStatusDisplay() }
        ];
    }

    function buildOutcomeLevelDisplay() {
        if (review?.outcomeLevel == null) {
            return null;
        }

        return (
            <StatusBlock background={review.outcomeLevel.colour}>
                {`Level ${review.outcomeLevel.number} - ${review.outcomeLevel.name}`}
            </StatusBlock>
        );
    }

    function renderTopic(topic) {
        let topicContent;

        // A topic the API withholds arrives with neither its comment nor its captured values.
        if (topic.accessible === false) {
            topicContent = (
                <div className="spacer-bottom">
                    <NotificationInline
                        allowClose={false}
                        show
                        text={inaccessibleTopicMessage}
                        intent="warning"
                    />
                </div>
            );
        } else {
            const SnapshotRenderer = topicSnapshotRenderers[topic.topicKey];

            // The comment is handed to the snapshot rather than rendered above it, so the comment
            // and the captured values share one table and one spacing rhythm.
            const commentField = {
                key: "topic-comment",
                header: "Topic comment",
                value: topic.comment ?? "-",
                fullWidth: true
            };

            if (SnapshotRenderer == null) {
                topicContent = (
                    <div className="spacer-bottom-small">
                        <DataDisplayTable loading={loading} displayInRows={false} data={[commentField]} />
                    </div>
                );
            } else {
                topicContent = (
                    <div className="spacer-bottom-small">
                        <SnapshotRenderer data={topic.data} loading={loading} leadingFields={[commentField]} />
                    </div>
                );
            }
        }

        return (
            <Fragment key={topic.topicKey}>
                <h2>{topic.topicName}</h2>
                {topicContent}
            </Fragment>
        );
    }

    return (
        <Fragment>
            <div className="row">
                <h1 className={classNames({ "bp6-skeleton": loading })}>
                    {buildTitleDisplay()}
                </h1>
            </div>
            <div className="row">
                <div className="spacer-bottom-small">
                    <DataDisplayTable data={buildMetaFields()} displayInRows={false} columnsPerRow={4} loading={loading} />
                </div>
            </div>
            <div className="row">
                <h2>Outcome</h2>
                <DataDisplayTable loading={loading} displayInRows={false} data={[
                    {
                        key: "outcome-level",
                        header: "Outcome level",
                        value: buildOutcomeLevelDisplay() ?? "-",
                        fullWidth: true
                    },
                    {
                        key: "outcome-summary",
                        header: "Outcome summary",
                        value: review?.outcomeSummary ?? "-",
                        fullWidth: true
                    },
                    {
                        key: "next-review-date",
                        header: "Next review date",
                        value: <DateToLocal format={dateFormat}>{review?.nextReviewDate}</DateToLocal>,
                        fullWidth: true
                    }
                ]} />
                {(review?.topics ?? []).map(renderTopic)}
                <ShowHide
                    evaluator={showSignOff}
                    show={(
                        <Fragment>
                            <h2>Sign Off</h2>
                            <ShowHide
                                evaluator={signOffMethodSentence != null}
                                show={(
                                    <p className="bp6-text-muted">{signOffMethodSentence}</p>
                                )}
                            />
                            {/* The two signatures are read side by side, and each column is its
                                label followed by its box. A label that wraps pushes only its own
                                box down, so both labels are held to the name alone: anything
                                further about the sign-off belongs in the sentence above, which
                                has the full width to wrap into. */}
                            <div className="completed-review-signatures spacer-bottom">
                                <ShowHide
                                    evaluator={review?.signOff?.managerSignature != null}
                                    show={(
                                        <div>
                                            <p className="spacer-bottom-medium"><span className="bold">Manager</span> - {review?.completedBy}</p>
                                            <FormCanvas
                                                readOnly
                                                imageSource={review?.signOff?.managerSignature?.url}
                                                width="100%"
                                                headingText={null}
                                                helperText={null}
                                                showDate
                                                signedDate={review?.completedDate}
                                            />
                                        </div>
                                    )}
                                />
                                <ShowHide
                                    evaluator={review?.signOff?.employeeSignedOffDate != null}
                                    show={(
                                        <div>
                                            <p className="spacer-bottom-medium"><span className="bold">Employee</span> - {review?.employee?.name}</p>
                                            <ShowHide
                                                evaluator={employeeSignature != null}
                                                show={(
                                                    <FormCanvas
                                                        readOnly
                                                        imageSource={employeeSignature?.url}
                                                        width="100%"
                                                        headingText={null}
                                                        helperText={null}
                                                        showDate
                                                        signedDate={review?.signOff?.employeeSignedOffDate}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                            <ShowHide
                                evaluator={signOffActions != null}
                                show={signOffActions}
                            />
                        </Fragment>
                    )}
                />
            </div>
        </Fragment>
    );
}

CompletedReviewDetail.propTypes = {
    review: PropTypes.object,
    loading: PropTypes.bool,
    linkEmployee: PropTypes.bool,
    showSignOff: PropTypes.bool,
    signOffActions: PropTypes.node,
    title: PropTypes.string
};
